import { useMemo } from "react";
import { Modal } from "../../components/Modal";
import { DataTable, type Column } from "../../components/DataTable";
import type { LogReviewSessionDetail } from "../../types/logReview";

/**
 * Minimal shape needed by the popup — satisfied by both the main
 * `LogReviewRow` and the Insights `LogInsightRow`.
 */
export interface LogReviewDetailsRow {
  changeRequestId: string;
  sessionDetails: LogReviewSessionDetail[];
}

export interface LogReviewDetailsModalProps {
  open: boolean;
  row: LogReviewDetailsRow | null;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
}

/**
 * Per-CR "Details" popup opened from a reason link. Lists every server
 * session for the change request with its planned/actual window and
 * compliance verdict.
 */
export const LogReviewDetailsModal = ({
  open,
  row,
  onOpenChange,
  isLoading = false,
}: LogReviewDetailsModalProps) => {
  const columns = useMemo<Column<LogReviewSessionDetail>[]>(
    () => [
      { key: "lanUserId", cellClassName: "!whitespace-nowrap", align: "left", header: "LAN ID/User ID", headerAlign: "center" },
      { key: "serverNode", cellClassName: "!whitespace-nowrap", align: "left", header: "Server/Nodes", headerAlign: "center" },
      { key: "targetAccount", cellClassName: "!whitespace-nowrap", align: "left", header: "Target Account", headerAlign: "center" },
      {
        key: "plannedStart",
        cellClassName: "!whitespace-nowrap",
        header: "Planned Start",
        align: "center",
        headerAlign: "center",
      },
      {
        key: "plannedEnd",
        cellClassName: "!whitespace-nowrap",
        header: "Planned End",
        align: "center",
        headerAlign: "center",
      },
      {
        key: "actualStart",
        cellClassName: "!whitespace-nowrap",
        header: "Actual Start",
        align: "center",
        headerAlign: "center",
      },
      {
        key: "actualEnd",
        cellClassName: "!whitespace-nowrap",
        header: "Actual End",
        align: "center",
        headerAlign: "center",
      },
      {
        key: "status",
        align: "left",
        header: "Status",
        headerAlign: "center",
        // Reasons ride along inside the status string and can be long — let
        // them wrap rather than truncating away the reason.
        maxChars: false,
        render: (r) => (
          <span className="block max-w-[240px] break-normal whitespace-normal">
            {r.status}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={row ? `Details : ${row.changeRequestId}` : "Details"}
      variant="extraWide"
    >
      <DataTable<LogReviewSessionDetail>
        columns={columns}
        data={row?.sessionDetails ?? []}
        loading={isLoading}
        emptyMessage="No Data Available"
        rowKey={(_, index) => index}
      />
    </Modal>
  );
};
